// Lib
import React, { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, get } from 'lodash/fp';

// Utils
import delay from '../../../common/utils/lib/delay';
import emailValidator from '../../../common/validation/validators/type/emailValidator';

// Selectors
import { getCurrentUserEmail, isGuestSelector } from '../../user/currentUserSelector';

// Actions
import { submitContentReport } from './reportContentModalActions';

// Components
import StyledButton from '../../components/buttons/StyledButton';

// Constants
import {
    CONTENT_REPORT_CATEGORIES,
    CONTENT_REPORT_SOURCE,
} from '../../../common/contentReports/contentReportConstants';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';

import './ReportContentForm.scss';

const isEmailValid = (email: string) => !isEmpty(email) && !emailValidator(email);

const ReportContentForm = (props: { close: () => void }) => {
    const { close } = props;

    const isGuest = useSelector(isGuestSelector);
    const currentUserEmail = useSelector(getCurrentUserEmail);

    const currentBoardId = useSelector(getCurrentBoardId);

    const dispatch = useDispatch();

    const dispatchSubmitContentReport = ({
        boardId,
        email,
        reason,
        otherDetails,
        contactUser,
    }: {
        boardId: string;
        email: string;
        reason: string;
        otherDetails: string;
        contactUser: boolean;
    }) =>
        dispatch(
            submitContentReport({
                boardId,
                email,
                reason,
                otherDetails,
                contactUser,
                source: CONTENT_REPORT_SOURCE.USER,
            }),
        );

    const otherInputRef = React.useRef<HTMLInputElement>(null);
    const emailInputRef = React.useRef<HTMLInputElement>(null);

    const [submitting, setSubmitting] = React.useState(false);

    const [selectedReason, setSelectedReasonState] = React.useState('SPAM');
    const [validReason, setValidReason] = React.useState(true);
    const [reasonOther, setReasonOther] = React.useState('');

    const [contactUser, setContactUserState] = React.useState(0);
    const [email, setEmail] = React.useState(currentUserEmail || '');
    const [validEmail, setValidEmail] = React.useState(true);

    const showEmail = isGuest;

    const onSubmit = async () => {
        setSubmitting(true);

        dispatchSubmitContentReport({
            boardId: get(['params', 'boardId'], props) || currentBoardId,
            email,
            reason: selectedReason,
            otherDetails: reasonOther,
            contactUser: !!contactUser,
        });

        await delay(200);

        close();
    };

    const focusOtherInput = () => {
        if (!otherInputRef.current) return;
        otherInputRef.current.focus();
    };

    const focusEmailInput = () => {
        if (!emailInputRef.current) return;
        emailInputRef.current.focus();
    };

    const setSelectedReason = (reason: string) => {
        setSelectedReasonState(reason);

        const isValidReason = reason !== 'OTHER' || !isEmpty(reasonOther);

        setValidReason(isValidReason);
    };

    const onReasonChange = (event: ChangeEvent<HTMLInputElement>) => setSelectedReason(event.currentTarget.value);
    const onReasonOtherChange = (event: ChangeEvent<HTMLInputElement>) => {
        setReasonOther(event.currentTarget.value);
        setValidReason(!isEmpty(event.currentTarget.value));
    };

    const setContactUser = (shouldContactUser: number) => {
        setContactUserState(shouldContactUser);
        const isValidEmail = !shouldContactUser || isEmailValid(email);
        setValidEmail(isValidEmail);
    };

    const onContactUserChange = (event: ChangeEvent<HTMLInputElement>) =>
        setContactUser(parseInt(event.currentTarget.value, 10));
    const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEmail(event.currentTarget.value);
        setValidEmail(isEmailValid(event.currentTarget.value));
    };

    return (
        <div className="ReportContentForm">
            <h2>Report inappropriate content</h2>

            <section>
                <p>What&apos;s the reason you want to report this board?</p>

                <ul className="reasons-list">
                    <li>
                        <input
                            type="radio"
                            name="reason"
                            value={CONTENT_REPORT_CATEGORIES.SPAM}
                            id={CONTENT_REPORT_CATEGORIES.SPAM}
                            checked={selectedReason === CONTENT_REPORT_CATEGORIES.SPAM}
                            onChange={onReasonChange}
                        />
                        <label htmlFor={CONTENT_REPORT_CATEGORIES.SPAM}>Looks like spam</label>
                    </li>
                    <li>
                        <input
                            type="radio"
                            name="reason"
                            value={CONTENT_REPORT_CATEGORIES.PHISHING}
                            id={CONTENT_REPORT_CATEGORIES.PHISHING}
                            checked={selectedReason === CONTENT_REPORT_CATEGORIES.PHISHING}
                            onChange={onReasonChange}
                        />
                        <label htmlFor={CONTENT_REPORT_CATEGORIES.PHISHING}>Looks like phishing</label>
                    </li>
                    <li className="other-reason-list-item">
                        <input
                            type="radio"
                            name="reason"
                            value={CONTENT_REPORT_CATEGORIES.OTHER}
                            id={CONTENT_REPORT_CATEGORIES.OTHER}
                            checked={selectedReason === CONTENT_REPORT_CATEGORIES.OTHER}
                            onClick={focusOtherInput}
                            onChange={onReasonChange}
                        />
                        <label htmlFor={CONTENT_REPORT_CATEGORIES.OTHER}>
                            Other:
                            <input
                                ref={otherInputRef}
                                className="other-input"
                                name="reasonOther"
                                placeholder="Reason..."
                                type="text"
                                value={reasonOther}
                                onFocus={() => setSelectedReason(CONTENT_REPORT_CATEGORIES.OTHER)}
                                onChange={onReasonOtherChange}
                            />
                        </label>
                    </li>
                </ul>
            </section>

            <section>
                <p>Do you want to be contacted about this issue?</p>

                <ul className="contact-list">
                    <li className="contact-user-list-item">
                        <input
                            type="radio"
                            name="contact-user"
                            value={1}
                            id="YES"
                            checked={!!contactUser}
                            onClick={focusEmailInput}
                            onChange={onContactUserChange}
                        />
                        <label htmlFor="YES">
                            Yes{showEmail && ':'}
                            {showEmail && (
                                <input
                                    ref={emailInputRef}
                                    className="email-input"
                                    name="contactEmail"
                                    placeholder="Email..."
                                    type="email"
                                    value={email}
                                    onFocus={() => setContactUser(1)}
                                    onChange={onEmailChange}
                                />
                            )}
                        </label>
                    </li>
                    <li>
                        <input
                            type="radio"
                            name="contact-user"
                            value={0}
                            id="NO"
                            checked={!contactUser}
                            onChange={onContactUserChange}
                        />
                        <label htmlFor="NO">No</label>
                    </li>
                </ul>
            </section>

            <StyledButton className="primary" disabled={submitting || !validReason || !validEmail} onClick={onSubmit}>
                Submit report
            </StyledButton>
        </div>
    );
};

export default ReportContentForm;
